import React, { useMemo, useState } from "react";
import { AlertTriangle, DatabaseBackup, Download, Eye, UploadCloud } from "lucide-react";
import AdminPageShell from "../components/AdminPageShell";

const cx = (...arr) => arr.filter(Boolean).join(" ");

const Card = ({ children, className }) => (
  <div className={cx("bg-white border border-slate-200 rounded-2xl", className)}>{children}</div>
);

const FieldBase =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30";

const SelectBase =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30";

const PrimaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors",
      className
    )}
  />
);

const SecondaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-sm font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors",
      className
    )}
  />
);

const DangerButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-sm font-semibold border border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100 transition-colors",
      className
    )}
  />
);

const BackupRestorePage = () => {
  const [backups, setBackups] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedBackupId, setSelectedBackupId] = useState("");

  const selectedBackup = useMemo(
    () => backups.find((b) => String(b.id) === String(selectedBackupId)) || null,
    [backups, selectedBackupId]
  );

  const handleCreateBackup = () => {
    const newBackup = {
      id: Date.now(),
      size: "5MB",
      date: new Date().toLocaleString(),
      triggeredBy: "Manual",
    };
    setBackups([newBackup, ...backups]);
    alert("Backup created!");
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleImport = () => {
    if (selectedFile) {
      alert(`Importing ${selectedFile.name}`);
      setSelectedFile(null);
    } else {
      alert("Please select a file to import.");
    }
  };

  const handleRestore = () => {
    if (!selectedBackup) {
      alert("Please select a backup timestamp first.");
      return;
    }
    // Placeholder for backend action
    alert(`Restoring backup from ${selectedBackup.date}`);
  };

  const handlePreview = () => {
    if (!selectedBackup) {
      alert("Please select a backup timestamp first.");
      return;
    }
    alert(`Previewing backup from ${selectedBackup.date}`);
  };

  return (
    <AdminPageShell
      title="Backup & Restore"
      description="Create backups, export/import data, and restore snapshots safely."
      chips={
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-bold text-slate-800">
            <DatabaseBackup className="h-4 w-4" />
            {backups.length} backups
          </span>
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold text-slate-700">
            Last: {backups[0]?.date ?? "—"}
          </span>
        </div>
      }
      actions={
        <>
          <SecondaryButton type="button">
            <Download className="w-4 h-4" />
            Export All
          </SecondaryButton>
          <PrimaryButton type="button" onClick={handleCreateBackup}>
            <DatabaseBackup className="w-4 h-4" />
            Create Backup
          </PrimaryButton>
        </>
      }
    >
      {/* Full DB Backup */}
      <Card className="p-5 md:p-6 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
              <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">Full DB Backup</h2>
            </div>
            <p className="mt-1 text-sm text-slate-600">Generate a snapshot of your database and store it securely.</p>
          </div>
          <PrimaryButton type="button" onClick={handleCreateBackup}>
            Create Manual Backup Now
          </PrimaryButton>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            "Schedule Auto Backup (Daily / Weekly / Monthly)",
            "Download Full SQL Dump",
            "Store Backup to Cloud (Google Drive, Dropbox, S3)",
            "Log Backup Size, Date & Trigger Source",
          ].map((t) => (
            <div key={t} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              {t}
            </div>
          ))}
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Recent Backups</p>
            <p className="text-sm text-slate-600">
              Total: <span className="font-bold text-slate-900">{backups.length}</span>
            </p>
          </div>

          <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-black uppercase tracking-wider text-slate-600">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-black uppercase tracking-wider text-slate-600">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-black uppercase tracking-wider text-slate-600">Triggered By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {backups.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-6 py-10 text-center text-sm text-slate-600">
                      No backups yet. Create your first backup to see it here.
                    </td>
                  </tr>
                ) : (
                  backups.map((b) => (
                    <tr key={b.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 text-sm font-semibold text-slate-900">{b.date}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{b.size}</td>
                      <td className="px-6 py-4 text-sm">
                        <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-bold text-slate-800">
                          {b.triggeredBy}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              </table>
            </div>
          </div>
        </div>
      </Card>

      {/* Export / Import Products */}
      <Card className="p-5 md:p-6 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
              <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">Export / Import Products</h2>
            </div>
            <p className="mt-1 text-sm text-slate-600">Move product data in and out with validation and rollback options.</p>
          </div>
          <SecondaryButton type="button">
            <Download className="w-4 h-4" />
            Export Products
          </SecondaryButton>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Import File</label>
            <input
              type="file"
              accept=".csv, application/json"
              onChange={handleFileChange}
              className={FieldBase}
            />
            <p className="mt-2 text-xs text-slate-500">Accepted: CSV, JSON</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Selected</p>
            <p className="mt-2 text-sm font-bold text-slate-900 truncate">{selectedFile?.name ?? "No file selected"}</p>
            <p className="mt-1 text-xs text-slate-600">{selectedFile ? `${Math.ceil(selectedFile.size / 1024)} KB` : ""}</p>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-end gap-3 flex-wrap">
          <SecondaryButton type="button" onClick={() => setSelectedFile(null)} disabled={!selectedFile}>
            Clear
          </SecondaryButton>
          <PrimaryButton type="button" onClick={handleImport}>
            <UploadCloud className="w-4 h-4" />
            Import Selected File
          </PrimaryButton>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          {["Field Mapping Tool", "Error Logs & Validation Check", "Partial Import Rollback Option"].map((t) => (
            <div key={t} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
              {t}
            </div>
          ))}
        </div>
      </Card>

      {/* Restore Snapshot */}
      <Card className="p-5 md:p-6 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
              <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">Restore Snapshot</h2>
            </div>
            <p className="mt-1 text-sm text-slate-600">Restore your database from a selected backup.</p>
          </div>
          <span className="inline-flex items-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2 text-xs font-bold text-amber-800">
            <AlertTriangle className="h-4 w-4" />
            Restoring is destructive
          </span>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Backup Timestamp</label>
            <select
              className={SelectBase}
              value={selectedBackupId}
              onChange={(e) => setSelectedBackupId(e.target.value)}
            >
              <option value="">Select Backup Timestamp</option>
              {backups.map((b) => (
                <option key={b.id} value={String(b.id)}>
                  {b.date}
                </option>
              ))}
            </select>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Selection</p>
            <p className="mt-2 text-sm font-bold text-slate-900 truncate">{selectedBackup?.date ?? "—"}</p>
            <p className="mt-1 text-xs text-slate-600">{selectedBackup ? `Size: ${selectedBackup.size} • Trigger: ${selectedBackup.triggeredBy}` : "Pick a backup to restore or preview."}</p>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-end gap-3 flex-wrap">
          <SecondaryButton type="button" onClick={handlePreview}>
            <Eye className="w-4 h-4" />
            Preview
          </SecondaryButton>
          <DangerButton type="button" onClick={handleRestore}>
            Restore Full Database
          </DangerButton>
        </div>
      </Card>

      {/* Logs & Monitoring */}
      <Card className="p-5 md:p-6">
        <div className="flex items-center gap-2">
          <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
          <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">Backup Logs & Monitoring</h2>
        </div>
        <p className="mt-1 text-sm text-slate-600">Audit and monitor backup/restore events.</p>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {["View All Backup Activities", "Backup Failure Alerts", "Restore History Logs", "Admin Who Performed Each Backup or Restore"].map((t) => (
            <div key={t} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              {t}
            </div>
          ))}
        </div>
      </Card>
    </AdminPageShell>
  );
};

export default BackupRestorePage;
